/**
 * Copyright © 2025-2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    Button,
    Typography,
    TextField,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    Divider,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    TableSortLabel,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import apiHeaders from "./apiHeaders";
import { handleSessionExpiredClose } from "./logout";

const MIN_LOG_DATE = new Date(2022, 0, 1);
const SEARCH_DEBOUNCE_MS = 400;
const DEFAULT_ROWS_PER_PAGE = 10;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

/**
 * Event Type values a log row can carry (see `DatabaseConnector.Entities.LogEventType` on the API
 * side). Used to populate the grid's Event Type filter dropdown - kept in sync with the backend
 * constants rather than free text, so a selected filter can always match a real row.
 */
const EVENT_TYPES = ["Info", "Warn", "Error", "Exception"];

/**
 * Columns the logs grid can be sorted by server-side (see `SyncLogsController.GetLogsPaged`'s
 * `sortBy` query param). "timestamp" is the default - matches the sibling Rillet connector's
 * logs grid, whose Id column likewise has no sort control of its own.
 */
const SORTABLE_COLUMNS = { TRANSACTION_ID: "transactionid", EVENT_TYPE: "eventtype", TIMESTAMP: "timestamp" };

/**
 * Builds the short, human-readable summary shown in the table's "Message" column from a log's
 * `logMessage` payload (see `DatabaseConnector.Entities.LogMessage` on the API side: it carries
 * a `transactionType` such as "SyncQuotes" and a `transactionDetails` status such as "Error").
 * The full payload, including the raw `transactiondata`, is only shown in the Details dialog and
 * the table's "Data" column.
 */
const summarizeLogMessage = (logMessage) => {
    if (!logMessage) return "-";
    const parts = [logMessage.transactionType, logMessage.transactionDetails].filter(Boolean);
    return parts.length > 0 ? parts.join(" – ") : "-";
};

const formatTimestamp = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

/** Short, single-line preview of a log's raw `transactiondata` for the table's "Data" column. */
const truncateJson = (data, maxLen = 50) => {
    if (data == null) return "-";
    const str = typeof data === "string" ? data : JSON.stringify(data);
    return str.length > maxLen ? `${str.substring(0, maxLen)}...` : str;
};

function Logger() {
    // Loads immediately for today's date, like the sibling Rillet connector's logs grid - there is
    // no separate search form to submit before the grid appears.
    const [activeDate, setActiveDate] = useState(() => dayjs());
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const [logs, setLogs] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const searchDebounceRef = useRef(null);

    const [eventTypeFilter, setEventTypeFilter] = useState("");
    const [sortBy, setSortBy] = useState(SORTABLE_COLUMNS.TIMESTAMP);
    const [sortDescending, setSortDescending] = useState(true);

    const [selectedLog, setSelectedLog] = useState(null);
    const [copied, setCopied] = useState(false);

    const tenantId = sessionStorage.getItem("tenantId");

    // Fetches one page of logs for the active date from the server-side paginated endpoint,
    // filtered by Transaction Id (debouncedSearchTerm) and/or Event Type, and sorted per
    // sortBy/sortDescending - mirroring the sibling Rillet connector's logs grid, whose date is
    // likewise just another in-grid filter rather than a separate search step.
    const fetchLogs = useCallback(async () => {
        if (!activeDate) return;
        setLoading(true);
        setError("");
        try {
            const params = new URLSearchParams({
                tenantId,
                date: activeDate.format("YYYY-MM-DD"),
                pageNumber: String(page + 1),
                pageSize: String(rowsPerPage),
                sortBy,
                sortDescending: String(sortDescending),
            });
            if (debouncedSearchTerm) {
                params.set("searchTerm", debouncedSearchTerm);
            }
            if (eventTypeFilter) {
                params.set("eventTypeFilter", eventTypeFilter);
            }

            const response = await fetch(
                `${process.env.REACT_APP_CONNECTOR_API_URL}/api/logs/paged?${params.toString()}`,
                {
                    method: "GET",
                    headers: {
                        ...apiHeaders,
                        Authorization: `${sessionStorage.getItem("token")}`,
                    },
                }
            );

            if (response.status === 401) {
                handleSessionExpiredClose();
                return;
            }
            if (!response.ok) {
                throw new Error("Failed to fetch logs.");
            }

            const data = await response.json();
            setLogs(data.items || []);
            setTotalCount(data.totalCount || 0);
        } catch (err) {
            setError("An error occurred while fetching logs.");
            setLogs([]);
            setTotalCount(0);
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [tenantId, activeDate, page, rowsPerPage, debouncedSearchTerm, eventTypeFilter, sortBy, sortDescending]);

    // Re-fetch on mount and whenever the date, page, page size, search term, Event Type filter, or
    // sort changes - there is no separate submit step.
    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleSearchTermChange = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setPage(0);
            setDebouncedSearchTerm(value);
        }, SEARCH_DEBOUNCE_MS);
    };

    const handleEventTypeFilterChange = (event) => {
        setEventTypeFilter(event.target.value);
        setPage(0);
    };

    const handleDateFilterChange = (newValue) => {
        setActiveDate(newValue);
        setPage(0);
        setError(newValue ? "" : "Enter the required date.");
    };

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDescending((current) => !current);
        } else {
            setSortBy(column);
            setSortDescending(true);
        }
        setPage(0);
    };

    const handleChangePage = (_event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle Download prompt (full day's logs as a PDF - unaffected by table pagination/search).
    const handleDownload = async () => {
        if (!activeDate) {
            setError("Enter the required date.");
            return;
        }

        setError("");
        try {
            const response = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/logs/generate-pdf?tenantId=${tenantId}&date=${activeDate.format("YYYY-MM-DD")}`,
                {
                    method: "GET",
                    headers: {
                        ...apiHeaders,
                        "Authorization": `${sessionStorage.getItem('token')}`
                    },
                }
            );
            if (response.status === 401) {
                handleSessionExpiredClose();
                return;
            }
            if (!response.ok) {
                throw new Error("Failed to fetch logs.");
            }
            const blob = await response.blob();
            const fileHandle = await window.showSaveFilePicker({
                suggestedName: `logs_${activeDate.format("YYYY-MM-DD")}.pdf`, // Default file name
                types: [
                    {
                        description: 'PDF Files',
                        accept: { 'application/pdf': ['.pdf'] },
                    },
                ],
            });

            // Write the file to the chosen location
            const writableStream = await fileHandle.createWritable();
            await writableStream.write(blob);
            await writableStream.close();
        }
        catch (err) {
            setError("An error occurred while fetching logs.");
            console.error(err);
        }
    };

    const handleViewDetails = (log) => {
        setSelectedLog(log);
        setCopied(false);
    };
    const handleCloseDetails = () => setSelectedLog(null);

    const handleCopyDetails = () => {
        if (!selectedLog?.logMessage) return;
        const text = JSON.stringify(selectedLog.logMessage, null, 2);
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection="column" gap={2} p={2}>
                {/* index.css applies `line-height: var(--zamp-leading-headline) !important` (1, tight)
                    to every heading app-wide as Zamp branding; the doubled "&&" selector raises
                    specificity so this page-scoped override can still win. */}
                <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", textAlign: "center", '&&': { lineHeight: '1.6 !important' } }}>
                    Logs
                </Typography>

                <Paper elevation={6} sx={{ boxShadow: 4, borderRadius: 2, p: 3, maxWidth: 1250, width: "100%", mx: "auto", mt: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2, flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                            <TextField
                                size="small"
                                label="Search by Transaction ID"
                                placeholder="Start typing to search"
                                value={searchTerm}
                                onChange={handleSearchTermChange}
                                sx={{ minWidth: 220 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel id="event-type-filter-label">Event Type</InputLabel>
                                <Select
                                    labelId="event-type-filter-label"
                                    value={eventTypeFilter}
                                    label="Event Type"
                                    onChange={handleEventTypeFilterChange}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {EVENT_TYPES.map((eventType) => (
                                        <MenuItem key={eventType} value={eventType}>{eventType}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <DatePicker
                                label="Date"
                                value={activeDate}
                                shouldDisableDate={(date) => date < MIN_LOG_DATE}
                                slotProps={{ textField: { size: "small" } }}
                                onChange={handleDateFilterChange}
                                sx={{ minWidth: 180 }}
                            />
                        </Box>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <Tooltip title="Download logs as PDF">
                                <span>
                                    <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />} onClick={handleDownload} disabled={!activeDate}>
                                        Download as PDF
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Refresh Logs">
                                <IconButton
                                    onClick={() => fetchLogs()}
                                    color="primary"
                                    aria-label="Refresh Logs"
                                    sx={{ border: "1px solid", borderColor: "grey.300", backgroundColor: "white", "&:hover": { backgroundColor: "grey.100" } }}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {error && (
                        <Typography color="error" sx={{ mb: 1, fontSize: 14 }}>{error}</Typography>
                    )}

                    <TableContainer sx={{ border: "1px solid rgba(224, 224, 224, 1)", borderRadius: 1, position: "relative" }}>
                        {loading && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "rgba(255,255,255,0.7)",
                                    zIndex: 2,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        )}
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ height: 50, backgroundColor: "grey.400" }}>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, borderRight: "1px solid rgba(224, 224, 224, 1)" }}>Id</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                        <TableSortLabel
                                            active={sortBy === SORTABLE_COLUMNS.TRANSACTION_ID}
                                            direction={sortBy === SORTABLE_COLUMNS.TRANSACTION_ID && !sortDescending ? "asc" : "desc"}
                                            onClick={() => handleSort(SORTABLE_COLUMNS.TRANSACTION_ID)}
                                        >
                                            Transaction Id
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                        <TableSortLabel
                                            active={sortBy === SORTABLE_COLUMNS.EVENT_TYPE}
                                            direction={sortBy === SORTABLE_COLUMNS.EVENT_TYPE && !sortDescending ? "asc" : "desc"}
                                            onClick={() => handleSort(SORTABLE_COLUMNS.EVENT_TYPE)}
                                        >
                                            Event Type
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                        <TableSortLabel
                                            active={sortBy === SORTABLE_COLUMNS.TIMESTAMP}
                                            direction={sortBy === SORTABLE_COLUMNS.TIMESTAMP && !sortDescending ? "asc" : "desc"}
                                            onClick={() => handleSort(SORTABLE_COLUMNS.TIMESTAMP)}
                                        >
                                            Timestamp
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, borderRight: "1px solid rgba(224, 224, 224, 1)" }}>Message</TableCell>
                                    <TableCell sx={{ fontWeight: "bold", fontSize: 16, borderRight: "1px solid rgba(224, 224, 224, 1)" }}>Data</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: "bold", fontSize: 16 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!loading && logs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">No logs found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    logs.map((log, index) => (
                                        <TableRow key={log.id} sx={{ backgroundColor: index % 2 === 0 ? "white" : "grey.100" }}>
                                            <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)" }}>{log.id}</TableCell>
                                            <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                <Tooltip title={log.transactionId || ""}>
                                                    <span>{log.transactionId || "-"}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)" }}>{log.eventType || "-"}</TableCell>
                                            <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)", whiteSpace: "nowrap" }}>
                                                {formatTimestamp(log.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)", maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                <Tooltip title={summarizeLogMessage(log.logMessage)}>
                                                    <span>{summarizeLogMessage(log.logMessage)}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                <Tooltip title={log.logMessage?.transactiondata ? JSON.stringify(log.logMessage.transactiondata, null, 2) : ""}>
                                                    <span>{truncateJson(log.logMessage?.transactiondata)}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="View log details">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        aria-label="View log details"
                                                        onClick={() => handleViewDetails(log)}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    />
                </Paper>
            </Box>

            <Dialog
                open={!!selectedLog}
                onClose={handleCloseDetails}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: "grey.200", py: 2, px: 3 }}>
                    {/* DialogTitle already renders an <h2>; render this as a <span> styled like an
                        h6 instead of nesting a second heading tag inside it. */}
                    <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                        Log Details{selectedLog?.id ? ` — #${selectedLog.id}` : ""}
                    </Typography>
                    <IconButton
                        onClick={handleCloseDetails}
                        size="small"
                        aria-label="Close log details"
                        sx={{ color: "grey.500", border: "1px solid", borderColor: "grey.300", backgroundColor: "white", "&:hover": { backgroundColor: "grey.100", color: "grey.800" } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ px: 3, py: 3 }}>
                    {selectedLog && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Transaction Id: {selectedLog.transactionId || "—"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Event Type: {selectedLog.eventType || "—"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Timestamp: {formatTimestamp(selectedLog.createdAt)}
                                </Typography>
                            </Box>

                            <Divider />

                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                                    Log Message
                                </Typography>
                                <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                                    <IconButton
                                        size="small"
                                        onClick={handleCopyDetails}
                                        aria-label="Copy log message to clipboard"
                                        sx={{ color: "grey.500", border: "1px solid", borderColor: "grey.200", "&:hover": { color: "primary.main", borderColor: "primary.main" } }}
                                    >
                                        {copied ? <CheckIcon sx={{ fontSize: 16 }} color="success" /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box
                                sx={{
                                    backgroundColor: "#f0f0f0",
                                    border: "1px solid",
                                    borderColor: "grey.200",
                                    borderRadius: 2,
                                    p: 2.5,
                                    maxHeight: 400,
                                    overflow: "auto",
                                }}
                            >
                                <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 13, lineHeight: 1.7 }}>
                                    {selectedLog.logMessage ? JSON.stringify(selectedLog.logMessage, null, 2) : "—"}
                                </pre>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </LocalizationProvider>
    );
}

export default Logger;
